import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import PercentIcon from "@mui/icons-material/Percent";
import { Collapse } from "@mui/material";
import ButtonCollapse from "components/shared/ButtonCollapse";
import StyledButton from "components/shared/StyledButton";
import { getCountries } from "countries";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { InvoicePercentage } from "store/invoicePercentages";
import { useAppSelector } from "store/store";

import variables from "../../../assets/styles/_variables.module.scss";
import styles from "./style.module.scss";

interface InvoicePercentageBlock {
    /**
     * Data of the percentage match.
     */
    percentage: InvoicePercentage;
    /**
     * Triggers modal to update line.
     */
    handleEdit: (percentage: InvoicePercentage) => Promise<void>;
    /**
     * Triggers modal to delete line.
     */
    handleDelete: (percentage: InvoicePercentage) => Promise<void>;
}

// Within InvoicePercentage settings page, the block that displays data of a percentage match.
const InvoicePercentageBlock = ({ percentage, handleEdit, handleDelete }: InvoicePercentageBlock) => {
    const [t] = useTranslation();
    const [isCollapseOpen, setIsCollapseOpen] = useState(false);

    const { channels } = useAppSelector((state) => state.channels);

    const channelFiltersIds = percentage.filters.channelIds;
    const countriesFiltersCodes = percentage.filters.countryCodes;

    /**
     * Returns array with only names of the channels chosen.
     */
    const channelNames = useMemo(() => {
        if (channelFiltersIds !== "all") {
            const chosenChannels = channels.filter((channel) => channelFiltersIds.includes(channel.id));
            return chosenChannels.map((channel) => channel.name);
        } else {
            return [t("InvoicePercentages.Block.AllChannels")];
        }
    }, [percentage, channels]);

    /**
     * Returns array with only names of the countries chosen.
     */
    const countriesNames = useMemo(() => {
        if (countriesFiltersCodes !== "all") {
            const chosenCountries = getCountries().filter((country) => countriesFiltersCodes.includes(country.code));
            return chosenCountries.map((country) => country.name);
        } else {
            return [t("InvoicePercentages.Block.AllCountries")];
        }
    }, [getCountries, percentage]);

    return (
        <div className={styles["block-wrapper"]}>
            <div className={styles["invoice-percentage-block"]} onClick={() => setIsCollapseOpen((prev) => !prev)}>
                <div className={styles["block-top"]}>
                    <span>{percentage.name}</span>
                    <div className={styles["percent"]}>
                        {" "}
                        <span>{Math.round(percentage.percentage) / 100}</span>{" "}
                        <PercentIcon style={{ width: "15px", fill: variables.info } as React.CSSProperties} />{" "}
                        <ButtonCollapse collapse={isCollapseOpen} sx={{ color: variables["foreground"] }} />
                    </div>
                </div>
            </div>
            <Collapse in={isCollapseOpen} timeout={"auto"}>
                <div className={styles["block-bottom"]}>
                    <div className={styles["filters-details"]}>
                        <div className={styles["filter"]}>
                            <span className={styles["filter-name"]}>
                                {t("InvoicePercentages.Block.ChosenChannels")}
                            </span>
                            <div className={styles["filter-chips"]}>
                                {channelNames.map((channel) => (
                                    <span className={styles["chip"]} key={`channel-${channel}`}>
                                        {channel}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className={styles["filter"]}>
                            <span className={styles["filter-name"]}>
                                {t("InvoicePercentages.Block.ChosenCountries")}
                            </span>
                            <div className={styles["filter-chips"]}>
                                {countriesNames.map((country) => (
                                    <span className={styles["chip"]} key={`country-${country}`}>
                                        {country}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={styles["action-buttons"]}>
                        <StyledButton
                            className={styles["update-button"]}
                            buttonType="outlined"
                            onClick={() => handleEdit(percentage)}
                        >
                            <DriveFileRenameOutlineOutlinedIcon />
                            {t("InvoicePercentages.Block.UpdateMatch")}
                        </StyledButton>
                        <StyledButton buttonType="outlined" onClick={() => handleDelete(percentage)}>
                            <DeleteOutlineOutlinedIcon />
                        </StyledButton>
                    </div>
                </div>
            </Collapse>
        </div>
    );
};

export default InvoicePercentageBlock;
